import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Camera, ChevronLeft, ChevronRight, CircleAlert, Expand, History,
  ImagePlus, KeyRound, QrCode, Replace, Star, Trash2, Upload, Wrench, X,
} from "lucide-react";
import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import type { ApartmentSummary, ApartmentStatus, CreateApartmentInput } from "@apartos/contracts";
import { api } from "../lib/api";
import { useDialog } from "../hooks/useDialog";
import { ModalPortal } from "./ModalPortal";
import { QrCodesPage } from "../pages/QrCodesPage";
import { getCurrentUser, hasPermission } from "../lib/auth";

const amenities = ["Wi-Fi", "Кондиционер", "Кухня", "Стиральная машина", "Smart TV", "Балкон", "Рабочее место", "Сейф"];

export function ApartmentModal({ apartment, onClose }: { apartment?: ApartmentSummary; onClose: () => void }) {
  const dialogRef = useDialog<HTMLElement>(onClose);
  const [tab, setTab] = useState<"details" | "stays" | "maintenance" | "photos" | "qr" | "locks">("details");
  const detailQuery = useQuery({
    queryKey: ["apartment", apartment?.id],
    queryFn: () => api.apartment(apartment!.id),
    enabled: Boolean(apartment),
  });
  const detail = detailQuery.data;
  const settingsQuery = useQuery({ queryKey: ["settings"], queryFn: api.settings });
  const locksQuery = useQuery({ queryKey: ["smart-locks"], queryFn: () => api.smartLocks(), enabled: Boolean(apartment) && tab === "locks" });
  const apartmentLocksQuery = useQuery({ queryKey: ["smart-locks", apartment?.id], queryFn: () => api.smartLocks(apartment!.id), enabled: Boolean(apartment) && tab === "locks" });
  const client = useQueryClient();
  const auth = useQuery({ queryKey: ["auth", "me"], queryFn: getCurrentUser, staleTime: 60_000 });
  const currentUser = auth.data && auth.data !== "bootstrap" ? auth.data : null;
  const canRemoteUnlock = hasPermission(currentUser, "smart_locks.remote_unlock");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [form, setForm] = useState<CreateApartmentInput>({
    number: apartment?.number ?? "", type: apartment?.type ?? "studio", floor: apartment?.floor ?? 2,
    status: apartment?.status ?? "free", amenities: apartment?.amenities ?? [], notes: apartment?.notes ?? "",
  });
  const mutation = useMutation({
    mutationFn: () => apartment ? api.updateApartment(apartment.id, form) : api.createApartment(form),
    onSuccess: async () => { await Promise.all([client.invalidateQueries({ queryKey: ["apartments"] }), client.invalidateQueries({ queryKey: ["dashboard"] })]); onClose(); },
  });
  const deleteMutation = useMutation({
    mutationFn: () => api.deleteApartment(apartment!.id),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ["apartments"] }),
        client.invalidateQueries({ queryKey: ["dashboard"] }),
        client.invalidateQueries({ queryKey: ["qr-codes"] }),
      ]);
      onClose();
    },
  });
  const photoMutation = useMutation({
    mutationFn: (photos: string[]) => api.updateApartment(apartment!.id, { photos }),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ["apartments"] }),
        client.invalidateQueries({ queryKey: ["apartment", apartment?.id] }),
        client.invalidateQueries({ queryKey: ["dashboard"] }),
      ]);
    },
  });
  const bindLocksMutation = useMutation({
    mutationFn: (input: { lockIds: string[]; pilotEnabled: boolean }) => api.bindApartmentLocks(apartment!.id, input),
    onSuccess: async () => { await Promise.all([client.invalidateQueries({ queryKey: ["smart-locks"] }), client.invalidateQueries({ queryKey: ["smart-locks", apartment?.id] })]); },
  });
  const unlockMutation = useMutation({ mutationFn: api.remoteUnlock });
  const submit = (event: FormEvent) => { event.preventDefault(); mutation.mutate(); };
  const availableTypes = settingsQuery.data?.rates.fixedByType ?? [];
  const currentTypeMissing = apartment && !availableTypes.some((item) => item.type === apartment.type);
  const selectedRate = availableTypes.find((item) => item.type === form.type)?.nightlyRate ?? apartment?.nightlyRate;
  useEffect(() => {
    if (!apartment && availableTypes.length > 0 && !availableTypes.some((item) => item.type === form.type)) {
      setForm((current) => ({ ...current, type: availableTypes[0]!.type }));
    }
  }, [apartment, availableTypes, form.type]);
  const toggleAmenity = (amenity: string) => setForm((current) => ({
    ...current, amenities: current.amenities.includes(amenity)
      ? current.amenities.filter((item) => item !== amenity) : [...current.amenities, amenity],
  }));
  return <ModalPortal onClose={onClose}><section ref={dialogRef} className="booking-modal" role="dialog" aria-modal="true" aria-labelledby="apartment-modal-title">
    <header className="modal-header"><div><h2 id="apartment-modal-title">{apartment ? `Apartment ${apartment.number}` : "Новый апартамент"}</h2><p>Основные параметры и оснащение номера</p></div><button type="button" className="icon-button" onClick={onClose} aria-label="Закрыть"><X size={17} /></button></header>
    {apartment && <nav className="detail-tabs" aria-label="Карточка апартамента">
      <button className={tab === "details" ? "is-active" : ""} onClick={() => setTab("details")}>Параметры</button>
      <button className={tab === "stays" ? "is-active" : ""} onClick={() => setTab("stays")}><History size={14} /> История</button>
      <button className={tab === "maintenance" ? "is-active" : ""} onClick={() => setTab("maintenance")}><Wrench size={14} /> Обслуживание</button>
      <button className={tab === "photos" ? "is-active" : ""} onClick={() => setTab("photos")}><Camera size={14} /> Фото</button>
      <button className={tab === "qr" ? "is-active" : ""} onClick={() => setTab("qr")}><QrCode size={14} /> QR для гостей</button>
      <button className={tab === "locks" ? "is-active" : ""} onClick={() => setTab("locks")}><KeyRound size={14} /> Замки</button>
    </nav>}
    {tab === "details" ? <form onSubmit={submit}><div className="modal-body"><div className="form-grid">
      <label className="field"><span>Номер</span><input name="number" autoComplete="off" required maxLength={32} value={form.number} onChange={(event) => setForm({ ...form, number: event.target.value })} /></label>
      <label className="field"><span>Этаж</span><input name="floor" autoComplete="off" required type="number" inputMode="numeric" min="0" max="100" value={form.floor} onChange={(event) => setForm({ ...form, floor: Number(event.target.value) })} /></label>
      <label className="field"><span>Вариант апартамента</span><select name="type" value={form.type} onChange={(event) => setForm({ ...form, type: event.target.value as CreateApartmentInput["type"] })}>{currentTypeMissing && <option value={apartment.type}>{apartment.typeLabel} · архивный</option>}{availableTypes.map((item) => <option value={item.type} key={item.type}>{item.label} · ${item.nightlyRate}/ночь</option>)}</select></label>
      <label className="field"><span>Статус</span><select name="status" value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value as ApartmentStatus })}><option value="free">Свободен</option><option value="occupied">Проживает</option><option value="cleaning">Уборка</option><option value="repair">Ремонт</option><option value="blocked">Заблокирован</option></select></label>
      <label className="field field--wide"><span>Заметки</span><textarea name="notes" autoComplete="off" value={form.notes ?? ""} onChange={(event) => setForm({ ...form, notes: event.target.value })} /></label>
    </div>{selectedRate !== undefined && <div className="apartment-rate-preview"><span>Базовая цена выбранного варианта</span><strong>${selectedRate} / ночь</strong><small>Меняется в Настройки → Апартаменты</small></div>}<div className="amenity-picker"><span>Удобства</span><div>{amenities.map((item) => <button type="button" className={form.amenities.includes(item) ? "is-active" : ""} onClick={() => toggleAmenity(item)} key={item}>{item}</button>)}</div></div>
    {mutation.error && <div className="form-error" role="alert"><CircleAlert size={15} />{mutation.error.message}</div>}</div>
    <footer className="modal-footer">{apartment && (confirmDelete ? <><span className="modal-delete-confirm">Удалить апартамент без операционной истории?</span><button type="button" className="secondary-button" onClick={() => setConfirmDelete(false)}>Нет</button><button type="button" className="danger-button" disabled={deleteMutation.isPending} onClick={() => deleteMutation.mutate()}>{deleteMutation.isPending ? "Удаляем…" : "Да, удалить"}</button></> : <button type="button" className="danger-button" onClick={() => setConfirmDelete(true)}><Trash2 size={14} /> Удалить</button>)}<div className="modal-footer__spacer" /><button type="button" className="secondary-button" onClick={onClose}>Отмена</button><button className="primary-button" disabled={mutation.isPending || availableTypes.length === 0}>{mutation.isPending ? "Сохраняем…" : "Сохранить"}</button></footer>
    {deleteMutation.error && <div className="modal-inline-error"><CircleAlert size={14} />{deleteMutation.error.message}</div>}</form>
    : <><div className="modal-body detail-panel">
      {detailQuery.isPending && <div className="skeleton skeleton--table" />}
      {detailQuery.isError && <div className="form-error" role="alert"><CircleAlert size={15} />История не загрузилась</div>}
      {detail && tab === "stays" && <div className="detail-list">
        {detail.stays.map((stay) => <article key={stay.bookingId}><div><strong>{stay.guestName}</strong><span>Apt {detail.number} · {new Date(stay.checkInAt).toLocaleDateString("ru-RU")} — {new Date(stay.checkOutAt).toLocaleDateString("ru-RU")}</span></div><b>${stay.total}</b></article>)}
        {detail.stays.length === 0 && <p className="detail-empty">Проживаний пока нет</p>}
      </div>}
      {detail && tab === "maintenance" && <div className="detail-list">
        {detail.maintenance.map((task) => <article key={task.id}><div><strong>{task.assignee ?? "Не назначено"}</strong><span>{new Date(task.dueAt).toLocaleString("ru-RU")} · {task.notes ?? "Регламентная уборка"}</span></div><span className={`status-pill tone-${task.status === "completed" ? "green" : "orange"}`}>{task.status}</span></article>)}
        {detail.maintenance.length === 0 && <p className="detail-empty">Задач обслуживания пока нет</p>}
      </div>}
      {detail && tab === "photos" && <PhotoGallery
        apartmentNumber={detail.number}
        initialPhotos={detail.photos}
        isSaving={photoMutation.isPending}
        error={photoMutation.error?.message}
        onSave={(photos) => photoMutation.mutate(photos)}
      />}
      {detail && tab === "qr" && <QrCodesPage embedded apartmentId={detail.id} />}
      {detail && tab === "locks" && <ApartmentLocksPanel
        locks={locksQuery.data ?? []}
        selectedIds={(apartmentLocksQuery.data ?? []).map((lock) => lock.id)}
        pilotEnabled={(apartmentLocksQuery.data ?? []).some((lock) => lock.pilotEnabled)}
        saving={bindLocksMutation.isPending}
        error={bindLocksMutation.error?.message}
        onSave={(lockIds, pilotEnabled) => bindLocksMutation.mutate({ lockIds, pilotEnabled })}
        canRemoteUnlock={canRemoteUnlock}
        onUnlock={(lockId) => unlockMutation.mutate(lockId)}
        unlockError={unlockMutation.error?.message}
      />}
    </div><footer className="modal-footer"><button className="secondary-button" onClick={onClose}>Закрыть</button></footer></>}
  </section></ModalPortal>;
}

function ApartmentLocksPanel({ locks, selectedIds, pilotEnabled, saving, error, onSave, canRemoteUnlock, onUnlock, unlockError }: {
  locks: import("@apartos/contracts").SmartLockSummary[];
  selectedIds: string[];
  pilotEnabled: boolean;
  saving: boolean;
  error?: string;
  onSave: (lockIds: string[], pilotEnabled: boolean) => void;
  canRemoteUnlock: boolean;
  onUnlock: (lockId: string) => void;
  unlockError?: string;
}) {
  const [selected, setSelected] = useState<string[]>(selectedIds);
  const [pilot, setPilot] = useState(pilotEnabled);
  useEffect(() => { setSelected(selectedIds); setPilot(pilotEnabled); }, [selectedIds, pilotEnabled]);
  return <div className="access-panel">
    <div className="settings-callout"><KeyRound size={18} /><div><strong>Электронные замки апартамента</strong><span>Первый выбранный замок считается основным. Реальный TTLock работает только после включения пилота.</span></div></div>
    {locks.length === 0 && <p className="detail-empty">Замков пока нет. Включите Mock TTLock или синхронизируйте реальные замки в настройках.</p>}
    {locks.map((lock) => <div className="access-lock-row" key={lock.id}>
      <input type="checkbox" aria-label={`Привязать ${lock.name}`} checked={selected.includes(lock.id)} onChange={() => setSelected((current) => current.includes(lock.id) ? current.filter((id) => id !== lock.id) : [...current, lock.id])} />
      <span><strong>{lock.name}</strong><small>{lock.model ?? lock.providerLockId} · {lock.battery ?? "—"}% · {lock.hasGateway ? "gateway" : "без gateway"}</small></span>
      <b className={lock.isOnline ? "is-ready" : ""}>{lock.isOnline ? "Online" : "Offline"}</b>
      {canRemoteUnlock && lock.capabilities.remoteUnlock === true && <button type="button" className="danger-button" onClick={() => window.confirm(`Удалённо открыть ${lock.name}? Действие попадёт в аудит.`) && onUnlock(lock.id)}>Открыть</button>}
    </div>)}
    <label className="access-pilot-toggle"><input type="checkbox" checked={pilot} disabled={selected.length === 0} onChange={(event) => setPilot(event.target.checked)} /><span><strong>Разрешить реальную автоматическую выдачу PIN</strong><small>Пилот включается только для этого апартамента</small></span></label>
    {(error || unlockError) && <div className="form-error"><CircleAlert size={15} />{error ?? unlockError}</div>}
    <button type="button" className="primary-button" disabled={saving} onClick={() => onSave(selected, pilot)}>{saving ? "Сохраняем…" : "Сохранить замки"}</button>
  </div>;
}

function PhotoGallery({ apartmentNumber, initialPhotos, isSaving, error, onSave }: {
  apartmentNumber: string;
  initialPhotos: string[];
  isSaving: boolean;
  error?: string;
  onSave: (photos: string[]) => void;
}) {
  const [photos, setPhotos] = useState(initialPhotos);
  const [selected, setSelected] = useState(0);
  const [direction, setDirection] = useState<1 | -1>(1);
  const [lightbox, setLightbox] = useState(false);
  const addInput = useRef<HTMLInputElement>(null);
  const replaceInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setPhotos(initialPhotos);
    setSelected((index) => Math.min(index, Math.max(0, initialPhotos.length - 1)));
  }, [initialPhotos]);

  const commit = (next: string[], nextSelected = selected) => {
    setPhotos(next);
    setSelected(Math.min(nextSelected, Math.max(0, next.length - 1)));
    onSave(next);
  };
  const importFiles = async (event: ChangeEvent<HTMLInputElement>, replaceCurrent: boolean) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (!files.length) return;
    const accepted = files.filter((file) => file.type.startsWith("image/") && file.size <= 2_000_000);
    if (!accepted.length) return;
    const loaded = await Promise.all(accepted.map(readImage));
    if (replaceCurrent && photos.length) {
      const next = [...photos];
      next[selected] = loaded[0]!;
      commit(next);
    } else {
      commit([...photos, ...loaded], photos.length);
    }
  };
  const removeSelected = () => {
    if (!photos.length) return;
    commit(photos.filter((_, index) => index !== selected), Math.max(0, selected - 1));
  };
  const makeCover = () => {
    if (selected === 0 || !photos[selected]) return;
    const next = [...photos];
    const [cover] = next.splice(selected, 1);
    next.unshift(cover!);
    commit(next, 0);
  };
  const move = (step: number) => {
    if (!photos.length) return;
    setDirection(step < 0 ? -1 : 1);
    setSelected((index) => (index + step + photos.length) % photos.length);
  };
  const selectPhoto = (index: number) => {
    setDirection(index < selected ? -1 : 1);
    setSelected(index);
  };
  const activePhoto = photos[selected];

  return <div className="apartment-gallery">
    <input ref={addInput} className="sr-only" type="file" accept="image/*" multiple onChange={(event) => void importFiles(event, false)} />
    <input ref={replaceInput} className="sr-only" type="file" accept="image/*" onChange={(event) => void importFiles(event, true)} />
    <div className="gallery-toolbar">
      <div><strong>Галерея номера</strong><span>{photos.length} фото · первое изображение используется как обложка</span></div>
      <button className="primary-button" type="button" onClick={() => addInput.current?.click()}><ImagePlus size={15} /> Добавить</button>
    </div>
    {activePhoto ? <>
      <div className="gallery-workspace">
        <div className="gallery-stage">
          <img className="gallery-stage__backdrop" src={activePhoto} alt="" aria-hidden="true" />
          <img key={`${selected}-${activePhoto}`} className={direction === 1 ? "gallery-stage__image is-next" : "gallery-stage__image is-prev"} src={activePhoto} alt={`Апартамент ${apartmentNumber}, фото ${selected + 1}`} />
          <div className="gallery-counter">{selected + 1} / {photos.length}</div>
          <button className="gallery-arrow gallery-arrow--left" type="button" onClick={() => move(-1)} aria-label="Предыдущее фото"><ChevronLeft size={20} /></button>
          <button className="gallery-arrow gallery-arrow--right" type="button" onClick={() => move(1)} aria-label="Следующее фото"><ChevronRight size={20} /></button>
          <button className="gallery-expand" type="button" onClick={() => setLightbox(true)} aria-label="Открыть на весь экран"><Expand size={17} /></button>
        </div>
        <div className="gallery-thumbnails" role="list" aria-label="Фотографии апартамента">
          {photos.map((photo, index) => <button type="button" className={index === selected ? "is-active" : ""} onClick={() => selectPhoto(index)} key={`${photo.slice(-60)}-${index}`} aria-label={`Открыть фото ${index + 1}`}>
            <img src={photo} alt="" />
            {index === 0 && <span><Star size={10} /> Обложка</span>}
          </button>)}
        </div>
      </div>
      <div className="gallery-actions">
        <button className="secondary-button" type="button" disabled={selected === 0 || isSaving} onClick={makeCover}><Star size={14} /> Сделать обложкой</button>
        <button className="secondary-button" type="button" disabled={isSaving} onClick={() => replaceInput.current?.click()}><Replace size={14} /> Заменить</button>
        <button className="danger-button" type="button" disabled={isSaving} onClick={removeSelected}><Trash2 size={14} /> Удалить</button>
        <span className="gallery-save-state">{isSaving ? "Сохраняем…" : "Изменения сохраняются автоматически"}</span>
      </div>
    </> : <button className="photo-empty photo-empty--button" type="button" onClick={() => addInput.current?.click()}>
      <Upload size={28} /><strong>Добавьте фотографии номера</strong><span>JPG, PNG или WebP до 2 МБ. Можно выбрать несколько файлов.</span>
    </button>}
    {error && <div className="form-error" role="alert"><CircleAlert size={15} />{error}</div>}
    {lightbox && activePhoto && <div className="gallery-lightbox" role="dialog" aria-modal="true" aria-label={`Просмотр фотографий апартамента ${apartmentNumber}`} onMouseDown={(event) => event.target === event.currentTarget && setLightbox(false)}>
      <button className="gallery-lightbox__close" type="button" onClick={() => setLightbox(false)} aria-label="Закрыть просмотр"><X size={20} /></button>
      <button className="gallery-lightbox__arrow gallery-lightbox__arrow--left" type="button" onClick={() => move(-1)} aria-label="Предыдущее фото"><ChevronLeft size={28} /></button>
      <img src={activePhoto} alt={`Апартамент ${apartmentNumber}, фото ${selected + 1}`} />
      <button className="gallery-lightbox__arrow gallery-lightbox__arrow--right" type="button" onClick={() => move(1)} aria-label="Следующее фото"><ChevronRight size={28} /></button>
      <span>{selected + 1} / {photos.length}</span>
    </div>}
  </div>;
}

function readImage(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Не удалось прочитать изображение"));
    reader.readAsDataURL(file);
  });
}
